import { ArrowUpRight, MapPin } from 'lucide-react';
import type { PhotoMedia, PhotoPost } from '@/types';

/** Digits that change remount (and animate on desktop); unchanged slots stay put. */
function PhotoItemDigits({ value }: { value: string }) {
  return (
    <span className="photo-item-digits" aria-hidden>
      {value.split('').map((ch, i) => (
        <span key={`${i}-${ch}`} className="photo-item-digit">
          {ch}
        </span>
      ))}
    </span>
  );
}

/** Instagram location explore URL (place page + photos at that place). */
export function instagramLocationUrl(
  id: string | undefined,
  slug: string | undefined,
): string | null {
  const locId = String(id || '').trim();
  if (!locId) return null;
  const s = String(slug || '')
    .trim()
    .replace(/^\/+|\/+$/g, '');
  return s
    ? `https://www.instagram.com/explore/locations/${encodeURIComponent(locId)}/${encodeURIComponent(s)}/`
    : `https://www.instagram.com/explore/locations/${encodeURIComponent(locId)}/`;
}

export function resolvePhotoLocation(
  post: PhotoPost,
  slideIndex = 0,
): { label: string; href: string | null } | null {
  const postLocation = post.location?.trim() || '';
  const visibleMedia = (Array.isArray(post.media) ? post.media : []).filter(
    (m): m is PhotoMedia => Boolean(m) && m.hidden !== true,
  );
  const slide =
    visibleMedia[
      Math.min(Math.max(0, slideIndex), Math.max(0, visibleMedia.length - 1))
    ] ?? null;
  const label = slide?.location?.trim() || postLocation || '';
  if (!label) return null;
  return {
    label,
    href: instagramLocationUrl(
      slide?.locationId || post.locationId,
      slide?.locationSlug || post.locationSlug,
    ),
  };
}

/**
 * Location label.
 * - `meta` (desktop table): Listen-style external arrow
 * - `caption` (mobile under post text): map pin on the left, no arrow
 */
export function PhotoLocationLink({
  post,
  slideIndex = 0,
  className,
  variant = 'meta',
}: {
  post: PhotoPost;
  slideIndex?: number;
  className?: string;
  variant?: 'meta' | 'caption';
}) {
  const loc = resolvePhotoLocation(post, slideIndex);
  if (!loc) return null;

  const isCaption = variant === 'caption';
  const classes = [
    'photo-meta-location-link',
    isCaption ? 'photo-meta-location-link--caption' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const body = (
    <>
      {isCaption ? (
        <MapPin
          className="photo-meta-location-pin"
          size={14}
          aria-hidden
        />
      ) : null}
      <span className="photo-meta-location-label">{loc.label}</span>
      {!isCaption ? (
        <ArrowUpRight
          className="photo-meta-location-external"
          size={11}
          aria-hidden
        />
      ) : null}
    </>
  );

  if (loc.href) {
    return (
      <a
        className={classes}
        href={loc.href}
        target="_blank"
        rel="noopener noreferrer"
      >
        {body}
      </a>
    );
  }

  return <span className={classes}>{body}</span>;
}

/** Format / Item / Location (+ Views) table for a photo post. */
export function PhotoMetaDetails({
  post,
  frameCount,
  slideIndex = 0,
  className,
}: {
  post: PhotoPost;
  frameCount: number;
  /** Active carousel slide — drives the Location row and Item index. */
  slideIndex?: number;
  className?: string;
}) {
  const format = post.format?.trim() || '';
  const loc = resolvePhotoLocation(post, slideIndex);
  const views =
    typeof post.views === 'number' && Number.isFinite(post.views) && post.views >= 0
      ? post.views
      : null;
  const visibleMedia = (Array.isArray(post.media) ? post.media : []).filter(
    (m): m is PhotoMedia => Boolean(m) && m.hidden !== true,
  );
  const frames = frameCount > 0 ? frameCount : visibleMedia.length;
  const itemCurrent = String(slideIndex + 1).padStart(2, '0');
  const itemTotal = String(Math.max(frames, 1)).padStart(2, '0');
  const showItem = frames > 0;

  if (!format && !showItem && !loc && views == null) {
    return null;
  }

  return (
    <div
      className={['photo-meta-panel', className].filter(Boolean).join(' ')}
    >
      <dl className="photo-meta-list" aria-label="Photo details">
        {format ? (
          <div className="photo-meta-item">
            <dt>Format</dt>
            <dd>{format}</dd>
          </div>
        ) : null}
        {showItem ? (
          <div className="photo-meta-item">
            <dt>Item</dt>
            <dd>
              <span
                className="photo-item-nums"
                aria-label={`Item ${itemCurrent} of ${itemTotal}`}
              >
                <PhotoItemDigits value={itemCurrent} />
                <span className="photo-item-sep" aria-hidden>
                  {' / '}
                </span>
                <PhotoItemDigits value={itemTotal} />
              </span>
            </dd>
          </div>
        ) : null}
        {loc ? (
          <div className="photo-meta-item">
            <dt>Location</dt>
            <dd>
              <PhotoLocationLink post={post} slideIndex={slideIndex} />
            </dd>
          </div>
        ) : null}
        {views != null ? (
          <div className="photo-meta-item">
            <dt>Views</dt>
            <dd>{views.toLocaleString('en-US')}</dd>
          </div>
        ) : null}
      </dl>
    </div>
  );
}
